import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BookOpen, Trophy, Target, TrendingUp, ArrowRight, ShoppingCart, Receipt } from 'lucide-react';

export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [mySeries, setMySeries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/student/dashboard'),
      api.get('/student/my-series'),
      api.get('/student/orders')
    ]).then(([d, s, o]) => {
      setDashboard(d.data);
      setMySeries(s.data);
      setOrders(o.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-[#7551e8] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="student-dashboard">
      <div className="mb-8">
        <h1 className="font-['Outfit'] text-3xl font-semibold text-[#0A0A0A]">Welcome back, {user?.name || 'Student'}</h1>
        <p className="text-[#52525B] mt-1">Track your progress and continue practicing</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="p-5 border border-zinc-200" data-testid="dash-stat-series">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#F4F0FF] flex items-center justify-center"><BookOpen size={18} className="text-[#7551e8]" /></div>
            <div>
              <p className="text-xs text-[#52525B] font-medium">My Test Series</p>
              <p className="font-['Outfit'] text-2xl font-semibold">{dashboard?.total_series || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5 border border-zinc-200" data-testid="dash-stat-tests">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center"><Trophy size={18} className="text-green-600" /></div>
            <div>
              <p className="text-xs text-[#52525B] font-medium">Tests Completed</p>
              <p className="font-['Outfit'] text-2xl font-semibold">{dashboard?.total_tests_taken || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5 border border-zinc-200" data-testid="dash-stat-accuracy">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center"><Target size={18} className="text-blue-600" /></div>
            <div>
              <p className="text-xs text-[#52525B] font-medium">Accuracy</p>
              <p className="font-['Outfit'] text-2xl font-semibold">{dashboard?.accuracy || 0}%</p>
            </div>
          </div>
        </Card>
        <Card className="p-5 border border-zinc-200" data-testid="dash-stat-correct">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center"><TrendingUp size={18} className="text-amber-600" /></div>
            <div>
              <p className="text-xs text-[#52525B] font-medium">Total Correct</p>
              <p className="font-['Outfit'] text-2xl font-semibold">{dashboard?.total_correct || 0}/{dashboard?.total_questions || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Content Tabs */}
      <Tabs defaultValue="series" className="space-y-4">
        <TabsList>
          <TabsTrigger value="series" data-testid="tab-my-series">My Test Series</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Order History</TabsTrigger>
        </TabsList>

        <TabsContent value="series">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['Outfit'] text-xl font-medium text-[#0A0A0A]">My Test Series</h2>
            <Button variant="ghost" className="text-[#7551e8] text-sm" onClick={() => navigate('/')} data-testid="browse-more-btn">
              Browse More <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>

        {mySeries.length === 0 ? (
          <Card className="p-12 text-center border border-zinc-200" data-testid="no-series-message">
            <BookOpen size={48} className="mx-auto mb-4 text-zinc-300" />
            <p className="text-lg font-medium text-[#0A0A0A]">No test series purchased yet</p>
            <p className="text-sm text-[#52525B] mt-1 mb-4">Start your preparation by enrolling in a test series</p>
            <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={() => navigate('/')}>
              Explore Test Series
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mySeries.map(s => {
              const progress = s.total_tests > 0 ? Math.round((s.completed_tests / s.total_tests) * 100) : 0;
              return (
                <Card key={s.id} className="card-hover border border-zinc-200 overflow-hidden" data-testid={`my-series-${s.id}`}>
                  <div className="aspect-video bg-zinc-100 overflow-hidden">
                    <img src={s.thumbnail_url || 'https://images.pexels.com/photos/7683899/pexels-photo-7683899.jpeg?auto=compress&w=400&h=250&fit=crop'}
                      alt={s.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4 space-y-3">
                    <h3 className="font-['Outfit'] font-medium text-[#0A0A0A]">{s.title}</h3>
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs text-[#52525B]">
                        <span>{s.completed_tests}/{s.total_tests} Tests</span>
                        <span>{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2 bg-zinc-100 [&>div]:bg-[#7551e8]" />
                    </div>
                    <Button className="w-full bg-[#7551e8] hover:bg-[#5e3dc7] text-white"
                      onClick={() => navigate(`/test-series/${s.id}`)} data-testid={`continue-series-${s.id}`}>
                      {progress > 0 ? 'Continue' : 'Start'} <ArrowRight size={14} className="ml-2" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        </TabsContent>

        <TabsContent value="orders" data-testid="orders-tab-content">
          <h2 className="font-['Outfit'] text-xl font-medium text-[#0A0A0A] mb-4">Purchase History</h2>
          {orders.length === 0 ? (
            <Card className="p-8 text-center border border-zinc-200">
              <ShoppingCart size={40} className="mx-auto mb-3 text-zinc-300" />
              <p className="text-[#52525B]">No purchases yet</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {orders.filter(o => o.status === 'completed').map((order, i) => (
                <Card key={order.order_id || i} className="p-4 border border-zinc-200" data-testid={`order-${order.order_id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Receipt size={14} className="text-[#7551e8]" />
                        <span className="text-xs font-mono text-[#52525B]">{order.order_id}</span>
                        <Badge className="bg-green-100 text-green-700 border-0 text-xs">Completed</Badge>
                      </div>
                      <div className="text-sm text-[#0A0A0A]">
                        {(order.items || []).map(item => item.title).join(', ') || 'Test Series Purchase'}
                      </div>
                      <div className="text-xs text-[#52525B] mt-1">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                        {order.coupon_code && <span className="ml-2 text-[#7551e8]">Coupon: {order.coupon_code}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-['Outfit'] font-semibold text-lg">&#8377;{order.total || order.amount || 0}</p>
                      {order.invoice_number && (
                        <p className="text-xs text-[#52525B]">Invoice: <span className="font-mono">{order.invoice_number}</span></p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
